import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../services/api'
import { ChevronLeft, Download, Eye, MessageSquare } from 'lucide-react'
import { toast } from 'sonner'

interface ContactSubmission {
  id: string
  nome: string
  email: string
  mensagem: string
  createdAt: string
}

export default function SubmissionsList() {
  const { token } = useAuth()
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [selectedSubmission, setSelectedSubmission] = useState<ContactSubmission | null>(null)

  useEffect(() => {
    loadSubmissions()
  }, [token, page])

  const loadSubmissions = async () => {
    if (!token) return
    setIsLoading(true)
    try {
      const data = await api.getContactSubmissions(token, page)
      if (data && data.submissions) {
        setSubmissions(data.submissions)
        setTotal(data.total)
      } else {
        setSubmissions([])
        setTotal(0)
      }
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error)
      toast.error('Erro ao carregar mensagens')
    } finally {
      setIsLoading(false)
    }
  }

  const exportToCSV = () => {
    const headers = ['ID', 'Nome', 'Email', 'Mensagem', 'Data/Hora']
    const rows = submissions.map((s) => [
      s.id,
      s.nome,
      s.email,
      `"${s.mensagem.replace(/"/g, '""')}"`,
      new Date(s.createdAt).toLocaleString('pt-BR')
    ])
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n')
    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `contato_submissions_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    URL.revokeObjectURL(link.href)
    toast.success('Arquivo CSV exportado com sucesso!')
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const totalPages = Math.ceil(total / 50)

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#51A64D] mx-auto mb-4"></div>
          <p className="text-gray-500">Carregando mensagens...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/admin/dashboard" className="text-gray-400 hover:text-gray-600">
              <ChevronLeft size={20} />
            </a>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#51A64D]/10 rounded-lg flex items-center justify-center text-[#51A64D]">
                <MessageSquare size={20} />
              </div>
              <div>
                <h1 className="text-xl font-serif font-bold text-gray-900">Fale Conosco</h1>
                <p className="text-sm text-gray-500">Mensagens enviadas pelo formulário de contato</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-gray-400">Total de mensagens</p>
              <p className="text-lg font-bold text-[#51A64D]">{total}</p>
            </div>
            <button
              onClick={exportToCSV}
              disabled={submissions.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-[#51A64D] text-white text-sm rounded-lg hover:bg-[#458f3f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={16} />
              Exportar CSV
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {submissions.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
            <div className="text-6xl mb-4">📭</div>
            <p className="text-gray-500 font-medium">Nenhuma mensagem encontrada</p>
            <p className="text-sm text-gray-400 mt-2">
              As mensagens aparecerão aqui quando alguém enviar o formulário de contato.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Data/Hora</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Nome</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Email</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Mensagem</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {submissions.map((sub) => (
                    <tr key={sub.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedSubmission(sub)}>
                      <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                        {formatDate(sub.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {sub.nome}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {sub.email}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-md truncate">
                        {sub.mensagem}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedSubmission(sub)
                          }}
                          className="text-[#51A64D] hover:text-[#458f3f]"
                          title="Ver detalhes"
                        >
                          <Eye size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm text-gray-600 disabled:opacity-50 hover:text-[#51A64D] transition-colors"
                >
                  Anterior
                </button>
                <span className="text-sm text-gray-500">
                  Página {page} de {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 text-sm text-gray-600 disabled:opacity-50 hover:text-[#51A64D] transition-colors"
                >
                  Próxima
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de Detalhes */}
      {selectedSubmission && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setSelectedSubmission(null)}
        >
          <div
            className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#51A64D]/10 rounded-lg flex items-center justify-center">
                  <MessageSquare size={16} className="text-[#51A64D]" />
                </div>
                <h3 className="font-serif font-bold text-lg text-gray-900">Detalhes da Mensagem</h3>
              </div>
              <button
                onClick={() => setSelectedSubmission(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                <div>
                  <label className="text-xs text-gray-500 uppercase font-semibold">Nome</label>
                  <p className="text-gray-900 font-medium mt-1">{selectedSubmission.nome}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase font-semibold">Email</label>
                  <p className="text-gray-900 font-medium mt-1">{selectedSubmission.email}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-gray-500 uppercase font-semibold">Data de Envio</label>
                  <p className="text-gray-900 font-medium mt-1">{formatDate(selectedSubmission.createdAt)}</p>
                </div>
              </div>
              
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare size={16} className="text-[#51A64D]" />
                  <label className="font-semibold text-gray-700">Mensagem</label>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-[#51A64D]">
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{selectedSubmission.mensagem}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}